import styles from './paymentReceiptPDFPage.module.css'
import { Customer, SaleOperation, useCustomerLoaders, useSaleOperationLoaders, saleOperationHelpers, useSalesHooks, useSaleOperationHooks } from "../../../../sales"
import { useEffect, useMemo, useState } from 'react'
import { DocumentKey, PaymentType, CreditPayment } from '../../../state/types'
import { useDataTypesHooks, useDescribeHooks } from '../../../../../features/data-types'
import { PDFHeader } from '../../../../../features/ui'
import { firstItem } from '../../../../../utils/listUtils'
import { usePriceOutlineHooks, usePricesHooks } from '../../../../operations'
import { TableName } from '../../../../../tables'
import { toDate } from '../../../../../utils/typeUtils'
import { PaymentReceiptItem, PaymentReceiptRow } from './types'
import { usePaymentTypeLoaders } from '../../../loaders/paymentTypeLoaders'

type Props = {
    pageIndex: number
    dataType: SaleOperation | CreditPayment
    items: PaymentReceiptItem[]
    pagesAmount: number
}

export const PaymentReceiptPDFPage = (props: Props) => {
    const { pageIndex, dataType, items, pagesAmount } = props
    const isLastPage = pageIndex === pagesAmount - 1
    const isCreditPayment = useMemo(() => Object.prototype.hasOwnProperty.call(dataType, 'operationItems'), [dataType])

    const [customer, setCustomer] = useState<Customer>()
    const [operations, setOperations] = useState<SaleOperation[]>([])
    const [paymentType, setPaymentType] = useState<PaymentType>()
    const [rows, setRows] = useState<PaymentReceiptRow[]>([])

    const { getTable } = saleOperationHelpers
    const { describe } = useDescribeHooks()
    const { formatDate, formatPrice, formatIdentifier, formatPhone } = useDataTypesHooks()
    const pricesHooks = usePricesHooks()
    const { getDocumentFooter } = useSalesHooks()
    const { describePaymentItemName } = useSaleOperationHooks()
    const { getPriceOutline } = usePriceOutlineHooks()

    const customerLoaders = useCustomerLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const saleOperationLoaders = useSaleOperationLoaders()
    
    useEffect(() => {
        customerLoaders().loadCustomer(dataType.customerId).then(setCustomer)
        const loadedPaymentType = paymentTypeLoaders().findPaymentType(dataType.paymentTypeId)
        setPaymentType(loadedPaymentType)
        if (isCreditPayment) {
            const creditPayment = dataType as CreditPayment
            const operationIds = creditPayment.operationItems.map(operationItem => operationItem.operationId)
            saleOperationLoaders().loadSaleOperations(operationIds).then(setOperations)
        }
    }, [dataType])
    
    const loadRows = async () => {
        if (isCreditPayment) {
            const creditPayment = dataType as CreditPayment
            return operations.length > 0 ? items.map(item => {
                const operation = operations.find(operation => operation.id === item.operationId)!
                return {
                    paymentDate: formatDate(toDate(creditPayment.creationDate)),
                    operation: describe(getTable(operation), operation),
                    amount: formatPrice(item.amount),
                }
            }) : []
        } else {
            return items.map(item => ({
                paymentDate: formatDate(toDate(item.paymentDate)),
                paymentType: describePaymentItemName(item),
                amount: formatPrice(item.amount)
            }))
        }
    }

    useEffect(() => {
        loadRows().then(newRows => setRows(newRows))
    }, [dataType, isCreditPayment, operations])

    const title = useMemo(() => {
        const operationLabel = isCreditPayment ?
            'Cuenta Corriente - Cobro' :
            getTable(dataType as SaleOperation) === TableName.SALE_ORDERS ? 'Venta' : 'Acopio'
        return `Recibo - ${operationLabel} #${dataType.code}`
    }, [dataType, isCreditPayment])

    const columns = [
        { name: 'paymentDate', label: 'Fecha de Pago' },
        isCreditPayment ?
            { name: 'operation', label: 'Operación' } :
            { name: 'paymentType', label: 'Medio de Pago' },
        { name: 'amount', label: 'Monto Cancelado' }
    ]

    const summaryData = useMemo(() => {
        if (isCreditPayment) {
            const creditPayment = dataType as CreditPayment
            const initialDebt = creditPayment.initialDebt || operations.reduce((sum, operation) => sum + getPriceOutline(getTable(operation), operation).total, 0)
            const receivedAmount = creditPayment.amount
            const adjustedPaidAmount = pricesHooks.adjustment.remove(receivedAmount, paymentType?.yield || 0)
            const paymentTypeAdjustment = receivedAmount - adjustedPaidAmount
            const initialCredit = creditPayment.initialCredit || 0
            const debtToCancel = initialDebt - initialCredit + paymentTypeAdjustment
            const pendingAmount = receivedAmount - debtToCancel
            return {
                initialDebt,
                paymentTypeAdjustment,
                initialCredit,
                debtToCancel,
                receivedAmount,
                pendingAmount,
            }
        } else {
            const saleOperation = dataType as SaleOperation
            const price = getPriceOutline(getTable(saleOperation), saleOperation).total
            const adjustedAmount = saleOperation.adjustedAmount || 0
            const initialCredit = saleOperation.initialCredit || 0
            const roundingAmount = saleOperation.paymentItems.filter(paymentItem => paymentItem.isRounding).reduce((sum, paymentItem) => sum + paymentItem.amount, 0)
            const debtToCancel = price - adjustedAmount - initialCredit - roundingAmount
            // The overpaid amount isn't part of the paid amount: it's added back to show what was really received
            const overpaidAmount = saleOperation.overpaidAmount || 0
            const receivedAmount = (saleOperation.receivedAmount || saleOperation.paidAmount - initialCredit - roundingAmount) + overpaidAmount
            const pendingAmount = receivedAmount - debtToCancel
            return {
                price,
                adjustedAmount,
                initialCredit,
                roundingAmount,
                debtToCancel,
                receivedAmount,
                overpaidAmount,
                pendingAmount
            }
        }
    }, [isCreditPayment, dataType, operations, paymentType])

    const documentFooter = useMemo(() => getDocumentFooter(DocumentKey.PAYMENT_RECEIPT), [getDocumentFooter])

    return (
        <div className={styles.container}>
            <div className={styles.body}>
                <PDFHeader />

                {pageIndex === 0 && (
                    <>
                        <div className={styles.row}>
                            <span className={styles.title}>
                                {title}
                            </span>
                        </div>

                        <div className={`${styles.row} ${styles.alignStart}`}>
                            <div className={styles.infoSection}>
                                <span>Cliente: {customer?.name}</span>
                                <span>Dirección: {customer?.mainAddress?.address || '-'}</span>
                                <span>DNI/CUIT: {customer ? formatIdentifier(customer.identifier) : '-'}</span>
                                <span>Teléfono: {customer?.phones?.length ? formatPhone(firstItem(customer.phones)) : '-'}</span>
                            </div>
                        </div>

                        {paymentType && isCreditPayment && (
                            <div className={`${styles.row} ${styles.alignStart}`}>
                                <div className={styles.infoSection}>
                                    <span>Medio de Pago: {paymentType.name}</span>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className={styles.tableRow}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                {columns.map((col, i) => (
                                    <th key={i}>{col.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => (
                                <tr key={i}>
                                    {columns.map(column => (
                                        <td key={column.name}>
                                            {row[column.name as keyof PaymentReceiptRow]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={styles.pagination}>
                    {pageIndex + 1} / {pagesAmount}
                </div>

                {isLastPage && (
                    <div className={styles.summary}>
                        {isCreditPayment ? (
                            <>
                                <div className={styles.summaryRow}>
                                    <span>Deuda Total: </span>
                                    <span>{formatPrice(summaryData.initialDebt)}</span>
                                </div>

                                {summaryData.paymentTypeAdjustment !== 0  && (
                                    <div className={styles.summaryRow}>
                                        <span>{summaryData.paymentTypeAdjustment! < 0 ? 'Dto.' : 'Ajuste'} Medio de Pago: </span>
                                        <span>{formatPrice(summaryData.paymentTypeAdjustment)}</span>
                                    </div>
                                )}

                                {summaryData.initialCredit! > 0 && (
                                    <div className={styles.summaryRow}>
                                        <span>Crédito Previo: </span>
                                        <span>{formatPrice(summaryData.initialCredit)}</span>
                                    </div>
                                )}

                                <div className={styles.summarySeparator} />
                        
                                <div className={styles.summaryRow}>
                                    <span>Monto a Cancelar: </span>
                                    <span>{formatPrice(summaryData.debtToCancel)}</span>
                                </div>

                                <div className={`${styles.summaryRow} ${styles.bold}`}>
                                    <span>Monto Recibido: </span>
                                    <span>{formatPrice(summaryData.receivedAmount)}</span>
                                </div>
                                
                                <div className={styles.summarySeparator} />

                                <div className={styles.summaryRow}>
                                    <span>{summaryData.pendingAmount! > 0 ? 'Crédito a Favor' : 'Saldo Pendiente'}: </span>
                                    <span>{formatPrice(Math.abs(summaryData.pendingAmount!))}</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.summaryRow}>
                                    <span>Precio: </span>
                                    <span>{formatPrice(summaryData.price)}</span>
                                </div>

                                {summaryData.adjustedAmount! !== 0 && (
                                    <div className={styles.summaryRow}>
                                        <span>Notas de Crédito: </span>
                                        <span>{formatPrice(-summaryData.adjustedAmount!)}</span>
                                    </div>
                                )}

                                {summaryData.initialCredit! > 0 && (
                                    <div className={styles.summaryRow}>
                                        <span>Crédito Previo: </span>
                                        <span>{formatPrice(summaryData.initialCredit)}</span>
                                    </div>
                                )}

                                {summaryData.roundingAmount! > 0 && (
                                    <div className={styles.summaryRow}>
                                        <span>Redondeo: </span>
                                        <span>{formatPrice(-summaryData.roundingAmount!)}</span>
                                    </div>
                                )}

                                <div className={styles.summarySeparator} />

                                <div className={styles.summaryRow}>
                                    <span>Monto a Cancelar: </span>
                                    <span>{formatPrice(summaryData.debtToCancel)}</span>
                                </div>

                                <div className={`${styles.summaryRow} ${styles.bold}`}>
                                    <span>Monto Recibido: </span>
                                    <span>{formatPrice(summaryData.receivedAmount)}</span>
                                </div>

                                <div className={styles.summarySeparator} />

                                <div className={styles.summaryRow}>
                                    <span>{summaryData.pendingAmount! > 0 ? 'Crédito a Favor' : 'Saldo Pendiente'}: </span>
                                    <span>{formatPrice(Math.abs(summaryData.pendingAmount!))}</span>
                                </div>

                                {summaryData.overpaidAmount! > 0 && (
                                    <div className={styles.summaryNote}>
                                        El pago excedente quedó acreditado como crédito a favor del cliente.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                <div className={styles.footer}>
                    <div className={styles.separator} />
                    <div className={styles.footerRow}>
                        <span>{documentFooter}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}
